import { Modal } from "@9amhealth/shared";
import styled from "@emotion/styled";
import type { FC } from "react";
import React, { useEffect, useState } from "react";
import { IconCrossXCloseMissed } from "src/constants/icons";
import { NineActions, NineButton } from "@9amhealth/wcl/generated/react";
import Translate from "src/ui/components/Translate/Translate";
import { appStatusBarState } from "src/state/state";
import type { TranslationKey } from "src/types/translationKey";

const DESTRUCTIVE_CONFIRM_STYLE = {
  "--black-to-gradient-dusk": "var(--Accessible-Red, #D04734)"
} as React.CSSProperties;

const Content = styled.div`
  label: AppDialogContent;
  padding: 1.5rem 1rem 2rem;
  position: relative;

  h5 {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 500;
  }

  p {
    margin: 0.3rem 0 1rem;
    font-weight: 400;
  }

  nine-actions {
    padding-left: 2rem;
    padding-top: 1rem;
    width: 100%;
    justify-content: flex-end;
  }
`;

const Close = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  padding: 0.5rem;
  background-color: transparent;
  border: none;

  svg {
    width: 1.5rem;
    height: auto;
  }
`;

const MODAL_STYLE = {
  "--max-width": "500px"
} as React.CSSProperties;

// A centered alert floats in the middle of the screen, so the shared dialog
// wrapper's bottom safe-area padding only adds an unwanted gap on iOS.
const StyledModal = styled(Modal)`
  & > * {
    padding-bottom: 0;
  }
`;

const AppDialog: FC<{
  children: React.ReactNode;
  onCancel?: ({ via }: { via: "cancel" | "close" }) => void;
  onConfirm?: () => void;
  onClose?: () => void;
  confirmText?: TranslationKey;
  cancelText?: TranslationKey;
  confirmArrow?: boolean;
  contentStyle?: React.CSSProperties;
  destructive?: boolean;
  hideClose?: boolean;
}> = ({
  children,
  onCancel,
  onConfirm,
  onClose,
  confirmArrow,
  destructive = false,
  hideClose = false,
  ...props
}) => {
  const { confirmText, cancelText, contentStyle } = props;

  const [isOpen, setIsOpen] = useState(true);

  // Match the legacy behaviour of restoring the status bar theme on unmount.
  useEffect(() => {
    return () => appStatusBarState.resetThemeDefault();
  }, []);

  // Action callbacks fire immediately on the interaction; setting isOpen to
  // false plays the exit animation, after which onDidDismiss runs the cleanup.
  const handleCancel = (via: "cancel" | "close"): void => {
    onCancel?.({ via });
    setIsOpen(false);
  };

  const handleConfirm = (): void => {
    onConfirm?.();
    setIsOpen(false);
  };

  // Backdrop click / Escape route through the Modal's own dismiss.
  const handleWillDismiss = (): void => {
    onCancel?.({ via: "close" });
  };

  const handleDidDismiss = (): void => {
    onClose?.();
    appStatusBarState.resetThemeDefault();
  };

  return (
    <StyledModal
      isOpen={isOpen}
      variant="alert"
      style={MODAL_STYLE}
      onWillDismiss={handleWillDismiss}
      onDidDismiss={handleDidDismiss}
    >
      <Content style={contentStyle}>
        {!hideClose && (
          <Close
            type="button"
            aria-label="Close"
            onClick={() => handleCancel("close")}
          >
            <IconCrossXCloseMissed />
          </Close>
        )}
        {children}
        <NineActions
          style={{ display: onCancel || onConfirm ? "flex" : "none" }}
        >
          {onCancel && (
            <NineButton
              variant="ghost"
              arrow=""
              padding="equal"
              onClick={() => handleCancel("cancel")}
            >
              {cancelText ? (
                <Translate msg={cancelText} />
              ) : (
                <Translate msg="cancel" />
              )}
            </NineButton>
          )}
          {onConfirm && (
            <NineButton
              variant="fill"
              arrow={confirmArrow ? ">" : ""}
              color="black"
              padding="equal"
              style={destructive ? DESTRUCTIVE_CONFIRM_STYLE : undefined}
              onClick={handleConfirm}
            >
              {confirmText ? (
                <Translate msg={confirmText} />
              ) : (
                <Translate msg="confirm" />
              )}
            </NineButton>
          )}
        </NineActions>
      </Content>
    </StyledModal>
  );
};

export default AppDialog;
